import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { SubmitHandler, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import {
  brokerConnectModalAtom,
  guideModalAtom,
  technicalSupportModalAtom,
} from "@/Atoms/modal/DialogueAtoms";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { useUserContext } from "@/contexts/userContext";
import { useToastHelper } from "@/helpers/helperFunctions";
import { useTranslation } from "react-i18next";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Tabs, TabsContent } from "../ui/tabs";

import { Input } from "../ui/input";
import { Button } from "../ui/button";
import Link from "next/link";
import Loader from "../Loader";
import {
  useGetBrokerOauthSterling,
  useGetCtraderAccounts,
  useGetFennelAccounts,
  useGetPublicUrl,
  usePostRevokeBrokerKeys,
  usePostSubmitBrokerKeys,
} from "@/api/brokerApi";
import { useGetMainPricingPlanDetails } from "@/api/subscriptionApi";
import { BROKER_LOGO } from "@/config/logos";
import TrafficSignalPill from "./TrafficSignalPill";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import {
  brokersList,
  predictionMarketBrokersList,
} from "../blocks/connection/BrokerList";
import { capitalizeFirst, cn, formatCurrency } from "@/lib/utils";
import { Separator } from "../ui/separator";
import { FaCheckCircle, FaRegCheckCircle, FaSearch } from "react-icons/fa";
import { useRouter } from "next/router";
import RevokeModal from "./RevokeModal";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { Badge } from "../ui/badge";
import { useSubscription } from "@/contexts/SubscriptionContext";
import AnimatedButton from "./AnimatedButton";
import { HiOutlineRocketLaunch } from "react-icons/hi2";
import { useQueryClient } from "@tanstack/react-query";
import { useStrategyMode } from "@/contexts/StrategyModeContext";

interface IFormValues {
  apiKey?: string;
  apiSecret?: string;
  passphrase?: string;
  accountId?: string;
}

function BrokerConnectModal() {
  const connectForm = useForm<IFormValues>({
    mode: "onSubmit",
  });
  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom,
  );
  const [revokeOpen, setRevokeOpen] = useState<boolean>(false);
  const {
    user: userInfo,
    connectedBrokers,
    refreshConnectedBrokers,
    balanceLoading,
  } = useUserContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    refreshConnectedBrokers();
  }, []);

  const [SCTab, setSCTab] = useState<any>(
    (showConnectForm?.brokerId ?? Object.keys(connectedBrokers).length == 1)
      ? Object.keys(connectedBrokers)?.[0].toString()
      : "",
  );
  const router = useRouter();
  const { details } = useAppDetails();
  const { toast } = useToastHelper();
  const { t } = useTranslation("broker-page");

  const {
    mutateAsync: revokeBrokerKeys,
    isPending: isRevokeLoading,
    isSuccess: isRevokeSuccess,
    isError: isRevokeError,
    error: revokeError,
  } = usePostRevokeBrokerKeys();
  const {
    mutateAsync: submitBrokerKeys,
    isPending: isSubmitLoading,
    data: submitData,
    isSuccess: isSubmitSuccess,
  } = usePostSubmitBrokerKeys();

  const { mutateAsync: getCtraderAccounts, isPending: ctraderLoading } =
    useGetCtraderAccounts();

  const { mutateAsync: getFennelAccounts, isPending: fennelLoading } =
    useGetFennelAccounts();
  const { mutateAsync: getSterlingAccounts, isPending: sterlingLoading } =
    useGetBrokerOauthSterling();

  const { mutateAsync: getPublicUrl, isPending: publicUrlLoading } =
    useGetPublicUrl();

  const { brokerConnected } = useMemo(() => {
    const connectedBroker = connectedBrokers?.[SCTab];
    return {
      brokerConnected: !!connectedBroker,
      connectedBroker: connectedBroker,
    };
  }, [connectedBrokers, SCTab]);

  const isSaving = isSubmitLoading || isRevokeLoading || sterlingLoading;
  const [showGuideModal, setShowGuideModal] = useRecoilState(guideModalAtom);
  const [, setShowTechnicalRequest] = useRecoilState(technicalSupportModalAtom);
  const [accounts, setAccount] = useState<any[]>();
  const [fennelAccounts, setFennelAccount] = useState<any[]>();
  const { userType } = useSubscription();
  const [searchTerm, setSearchTerm] = useState("");
  const { mode } = useStrategyMode();
  const isPredictionMarket = mode === "prediction_market";

  // In prediction market mode show only prediction market brokers
  const activeBrokerList = isPredictionMarket
    ? predictionMarketBrokersList
    : brokersList;

  const { data: proPlan, isLoading: isLoadingPro } =
    useGetMainPricingPlanDetails("pro");
  const { data: elitePlan, isLoading: isLoadingElite } =
    useGetMainPricingPlanDetails("elite");

  const selectedBroker = useMemo(
    () =>
      [...brokersList, ...predictionMarketBrokersList].find(
        (b) => b.id === SCTab,
      ),
    [SCTab],
  );

  const formValues = connectForm.watch();

  const isFormValid = useMemo(() => {
    if (!selectedBroker) return true;

    try {
      const isApiKeyRequired = selectedBroker.defaultApiKey === undefined;
      const isApiSecretRequired = selectedBroker.defaultApiSecret === undefined;
      const isPassphraseRequired =
        selectedBroker.defaultPassphrase === undefined;

      if (isApiKeyRequired && !formValues.apiKey) return false;
      if (isApiSecretRequired && !formValues.apiSecret) return false;
      if (isPassphraseRequired && !formValues.passphrase) return false;
      if (SCTab === "30" && !formValues.accountId) return false;

      return true;
    } catch (e) {
      return true;
    }
  }, [selectedBroker, formValues, SCTab]);

  const handleRevokeBrokerKeys = async () => {
    await revokeBrokerKeys({
      userId: userInfo?.user_id,
      key: String(SCTab),
    });
  };

  const handleCtraderConnect = async () => {
    try {
      const response = await getCtraderAccounts({
        access_token: connectForm.getValues("apiKey"),
      });
      setAccount(response?.data);
    } catch {}
  };

  const handleFennelConnect = async () => {
    try {
      const response = await getFennelAccounts({
        access_token: connectForm.getValues("apiSecret"),
      });
      setFennelAccount(response?.data?.accounts);
    } catch {}
  };
  const handleSterlingConnect = async () => {
    try {
      const response = await getSterlingAccounts({
        user_id: userInfo?.user_id,
        username: connectForm.getValues("apiKey"),
        password: connectForm.getValues("passphrase"),
      });
      console.log(response);
      if (response?.redirect_url) {
        router.push(response?.redirect_url);
      }
    } catch {}
  };

  const handleSubmitBrokerKeys: SubmitHandler<IFormValues> = async (
    values,
    event,
  ) => {
    event?.preventDefault(); // Prevent form submission default behavior if applicable
    if (["19"].includes(SCTab)) {
      handlePublicConnect();
      return;
    }
    if (["26"].includes(SCTab)) {
      handleSterlingConnect();
      return;
    }
    const { apiKey, apiSecret, passphrase, accountId } =
      values as Required<IFormValues>;
    const resolvedSecret =
      brokersList.find((b) => b.id == SCTab)?.defaultApiSecret ?? apiSecret;
    const finalSecret =
      SCTab === "30"
        ? `${resolvedSecret ?? ""}^^${accountId ?? ""}`
        : resolvedSecret;
    try {
      await submitBrokerKeys({
        userId: userInfo?.user_id,
        key: brokersList.find((b) => b.id == SCTab)?.defaultApiKey ?? apiKey,
        secret: finalSecret,
        brokerId: SCTab,
        passphrase:
          brokersList.find((b) => b.id == SCTab)?.defaultPassphrase ??
          passphrase,
      });
    } catch (error: any) {
      toast({ title: error?.response?.data?.detail, variant: "destructive" });
    }

    queryClient.invalidateQueries({
      queryKey: ["get_live_deployments_aggregated_stats", userInfo?.user_id],
    });
  };

  const handlePublicConnect = async () => {
    try {
      const res = await getPublicUrl({ user_id: userInfo?.user_id });
      router.push(res?.data?.url);
    } catch (error: any) {
      toast({ title: error?.response?.data?.detail, variant: "destructive" });
    }
  };

  const changeTab = (key: string) => {
    setSCTab("UPGRADE");
  };
  useEffect(() => {
    connectForm.clearErrors();
    connectForm.reset();
  }, [SCTab]);

  useEffect(() => {
    if (isRevokeSuccess) {
      toast({ title: "Broker keys revoked successfully" });
      refreshConnectedBrokers();
      setShowConnectForm({ isOpen: false });
    } else if (isRevokeError && revokeError) {
      const errMessage = revokeError?.response?.data?.detail;
      if (
        errMessage !==
        "Request free access to evaluate or upgrade to the Pro plan for full access."
      ) {
        toast({
          title: errMessage || t("errorWhileDisconnectingTheBroker"),
          variant: "destructive",
        });
      } else {
        toast({
          title: t("errorWhileDisconnectingTheBroker"),
          variant: "destructive",
        });
      }
    }
  }, [isRevokeLoading, isRevokeSuccess, isRevokeError]);

  useEffect(() => {
    if (isSubmitSuccess) {
      toast({
        title: submitData?.data?.message,
      });
      if (submitData?.data?.redirect_url) {
        router.push(submitData?.data?.redirect_url);
      }
      connectForm.setValue("apiKey", "");
      connectForm.setValue("apiSecret", "");
      connectForm.setValue("passphrase", "");
      connectForm.setValue("accountId", "");
      refreshConnectedBrokers();
      setShowConnectForm({ isOpen: false });
    }
  }, [isSubmitLoading, isSubmitSuccess]);
  const renderInputField = (
    name: "passphrase" | "apiKey" | "apiSecret" | "accountId",
    label: string,
    placeholder: string,
    disabled = false,
    brokerConnectedString?: string,
  ) => (
    <FormField
      control={connectForm.control}
      name={name}
      rules={{ required: !disabled }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              autoComplete="off"
              disabled={disabled}
              placeholder={placeholder}
              {...field}
              value={
                disabled && brokerConnectedString
                  ? brokerConnectedString
                  : field.value || ""
              }
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <>
      <Dialog
        open={true}
        onOpenChange={() => {
          setShowConnectForm({ isOpen: false });
          connectForm.clearErrors();
          connectForm.reset();
        }}
      >
        <DialogContent className="max-w-5xl  py-0   sm:h-auto h-[100dvh]  overflow-y-scroll overflow-x-hidden px-8">
          <Form {...connectForm}>
            <form
              className="h-full xl:flex xl:flex-col w-full "
              method="post"
              onSubmit={
                brokerConnected
                  ? connectForm.handleSubmit(() => setRevokeOpen(true))
                  : connectForm.handleSubmit(handleSubmitBrokerKeys)
              }
              id="strategy-form"
            >
              <CardHeader className="px-0 w-full flex  flex-col gap-0 ">
                <CardTitle className="flex  justify-between pr-4">
                  <div className="flex justify-between w-full flex-col md:flex-row">
                    <div className="text-lg md:text-2xl whitespace-nowrap font-bold tracking-tight ">
                      {t("Connect Broker Account")}
                    </div>
                    <div className="flex text-xs font-bold text-muted-foreground justify-between gap-0">
                      {" "}
                      <TrafficSignalPill
                        text={`${
                          Object.keys(connectedBrokers)?.length
                        } ${t("Connected")}`}
                        stoppedText={t("No Broker Connected")}
                        className="text-muted-foreground font-bold"
                      ></TrafficSignalPill>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>

              <CardContent className="flex p-0 gap-8 min-h-[550px] flex-col md:flex-row">
                <div className="flex flex-col gap-4 mt-0 w-full md:w-2/3 lg:w-1/2 mx-auto max-h-[550px] ">
                  <p className="text-sm text-muted-foreground mb-2">
                    {t(
                      "Connect Level2 to one or more broker accounts via API to place trades automatically—even in paper accounts—using secure, encrypted API keys you control and can revoke anytime.",
                    )}
                  </p>

                  {/* Search Input */}
                  <div className="relative   px-2 pb-2 pr-2">
                    <Input
                      type="text"
                      placeholder={t("Search brokers...")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full rounded-lg border border-gray-600 bg-transparent px-3 py-2 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute right-3 top-2.5 h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                      />
                    </svg>
                  </div>

                  {/* Broker List */}
                  <div className="grid grid-cols-2  md:grid-cols-3 gap-2  overflow-y-auto px-2 pb-2 pr-2 rounded-md scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent relative ">
                    {balanceLoading && (
                      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/50 backdrop-blur-sm rounded-md">
                        <Loader />
                        <p className="text-sm font-medium text-muted-foreground animate-pulse mt-4">
                          {t("Fetching connections. Please wait …")}
                        </p>
                      </div>
                    )}
                    {activeBrokerList
                      .filter((broker) =>
                        broker.id !== "3" &&
                        broker.id !== "29" &&
                        broker.name
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()),
                      )
                      .map((broker) => {
                        const isSelected = SCTab === broker.id;
                        const cBalance =
                          connectedBrokers?.[Number(broker.id)]?.balance;
                        const isDisabled = broker.disabled;

                        const planWeights: Record<string, number> = {
                          free: 0,
                          free_access: 0,
                          pro: 1,
                          elite: 2,
                          enterprise: 2,
                        };
                        const userLevel =
                          planWeights[userType?.toLowerCase() || ""] ?? 0;
                        const brokerLevel =
                          planWeights[broker.plan?.toLowerCase() || ""] ?? 0;
                        const needsUpgradeForBroker = userLevel < brokerLevel;

                        return (
                          <div
                            key={broker.id}
                            onClick={() => {
                              if (isDisabled) return;
                              if (needsUpgradeForBroker && !cBalance) {
                                if (brokerLevel === 2) {
                                  setSCTab("ELITE");
                                } else {
                                  setSCTab("UPGRADE");
                                }
                                return;
                              }
                              setSCTab(broker.id);
                            }}
                            className={cn(
                              "cursor-pointer rounded-xl border p-5 h-[140px] overflow-hidden text-center transition-all flex flex-col gap-2 items-center justify-center relative shadow-sm",
                              isSelected
                                ? "border-blue-500 bg-blue-500/10"
                                : "border-gray-700 hover:border-gray-500 hover:bg-gray-800/40",
                              isDisabled && "opacity-50 hover:border-gray-700",
                            )}
                          >
                            {broker.logo && (
                              <img
                                src={broker.logo}
                                alt={broker.name}
                                className="mx-auto h-8  object-contain"
                              />
                            )}
                            {needsUpgradeForBroker && broker.plan && (
                              <div className="absolute top-2 right-2">
                                <AnimatedButton
                                  active={["pro", "elite"].includes(
                                    broker.plan,
                                  )}
                                  className={cn(
                                    "bg-[--accent-primary] p-0 w-fit h-fit px-2 rounded-full flex",
                                    broker.plan == "free" &&
                                      "bg-[--marketplace-green]",
                                    broker.plan == "pro" &&
                                      "bg-[--accent-primary]",
                                    broker.plan == "elite" &&
                                      "elite-gold-gradient text-black",
                                  )}
                                  onClick={() => {}}
                                >
                                  <Link href="/subscription">
                                    <div className="flex items-center gap-4 font-bold text-xs ">
                                      {capitalizeFirst(broker.plan)}
                                    </div>
                                  </Link>
                                </AnimatedButton>
                              </div>
                            )}
                            <div>
                              <p className="text-xs text-muted-foreground">
                                {broker.note}
                              </p>
                              {cBalance && (
                                <div className="flex  bottom-0 justify-center w-full h-8 flex-col items-center gap-1">
                                  <div className="flex gap-1 items-center">
                                    <p
                                      className={cn(
                                        "font-semibold text-xs",
                                        cBalance > 0
                                          ? "text-[--marketplace-green]"
                                          : cBalance < 0
                                            ? "text-[--marketplace-red]"
                                            : "text-gray-300",
                                      )}
                                    >
                                      {formatCurrency(
                                        cBalance,
                                        2,
                                        false,
                                        undefined,
                                        true,
                                      )}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {cBalance && (
                              <>
                                <div className="flex absolute bottom-0 justify-center  w-full h-6 bg-[--marketplace-green] items-center gap-1">
                                  <FaCheckCircle className="text-white text-xs" />
                                  <p className="text-xs font-bold">
                                    {t("Connected")}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                <Separator orientation="vertical"></Separator>
                <Tabs
                  onValueChange={(val) => {
                    changeTab(val);
                  }}
                  value={SCTab}
                  className=" md:w-1/2 "
                >
                  <TabsContent
                    value=""
                    className="items-center justify-center flex"
                  >
                    <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                      <p className="text-base font-semibold text-muted-foreground">
                        {t("Please select the broker you want to connect")}
                      </p>
                      <p className="text-base text-muted-foreground">
                        {t("Don't see your broker listed or have questions?")}
                      </p>
                      <p className="text-base font-regular text-foreground">
                        {t("Connect with our API experts")}
                      </p>
                      <Badge
                        className="text-base font-bold  px-4 cursor-pointer"
                        onClick={() => {
                          setShowConnectForm({ isOpen: false });
                          setTimeout(
                            () =>
                              setShowTechnicalRequest({
                                isOpen: true,
                                isRequest: false,
                                isContactSales: false,
                              }),
                            50,
                          );
                        }}
                      >
                        {t("Technical support")}
                      </Badge>
                    </div>
                  </TabsContent>

                  {/* Upgrade Tab */}
                  <TabsContent value={"UPGRADE"}>
                    <div className="flex flex-col gap-6 pt-4 px-4 h-full justify-center">
                      <div className="flex flex-col items-center gap-6 text-center">
                        <div className="w-16 h-16 rounded-full bg-[--accent-primary]/10 flex items-center justify-center mb-2">
                          <span className="text-3xl">
                            <HiOutlineRocketLaunch></HiOutlineRocketLaunch>
                          </span>
                        </div>
                        <div className="space-y-2">
                          <CardTitle className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[--accent-primary] to-blue-600">
                            {t("Unlock Live Trading")}
                          </CardTitle>
                          <CardDescription className="text-base max-w-md mx-auto">
                            {t(
                              "Automate your trading with top-tier live brokers. Upgrade to Pro for exclusive access.",
                            )}
                          </CardDescription>
                        </div>

                        <div className="flex flex-col gap-2">
                          {proPlan?.key_points
                            ?.slice(0, 4)
                            ?.map((point: string, index: number) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 font-semibold text-muted-foreground text-left"
                              >
                                <FaRegCheckCircle
                                  size={22}
                                  className="text-[--accent-secondary]"
                                />
                                <p>{point}</p>
                              </div>
                            ))}
                        </div>

                        <div className="mt-8 w-full max-w-sm flex  flex-col justify-center items-center">
                          <AnimatedButton
                            active
                            className="bg-[--accent-primary] flex rounded-3xl"
                            onClick={() => {
                              setShowConnectForm({ isOpen: false });
                            }}
                          >
                            <Link href="/subscription">
                              <div className="flex items-center gap-4 font-bold">
                                {t("Upgrade to Pro")}
                              </div>
                            </Link>
                          </AnimatedButton>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Elite Tab */}
                  <TabsContent value={"ELITE"}>
                    <div className="flex flex-col gap-6 pt-4 px-4 h-full justify-center">
                      <div className="flex flex-col items-center gap-6 text-center">
                        <div className="w-16 h-16 rounded-full elite-gold-gradient flex items-center justify-center mb-2 shadow-inner">
                          <span className="text-3xl">
                            <HiOutlineRocketLaunch className="text-black"></HiOutlineRocketLaunch>
                          </span>
                        </div>
                        <div className="space-y-2">
                          <CardTitle className="text-2xl font-bold elite-gold-text">
                            {t("Unlock Elite Trading")}
                          </CardTitle>
                          <CardDescription className="text-base max-w-md mx-auto">
                            {t(
                              "Access institutional-grade brokers and specialized direct market access. Upgrade to Elite for the ultimate trading experience.",
                            )}
                          </CardDescription>
                        </div>

                        <div className="flex flex-col gap-2">
                          {elitePlan?.key_points
                            ?.slice(0, 4)
                            ?.map((point: string, index: number) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 font-semibold text-muted-foreground text-left"
                              >
                                <FaRegCheckCircle
                                  size={22}
                                  className="text-[#bf953f]"
                                />
                                <p>{point}</p>
                              </div>
                            ))}
                        </div>

                        <div className="mt-8 w-full max-w-sm flex  flex-col justify-center items-center">
                          <AnimatedButton
                            active
                            className="elite-gold-button w-[200px] h-[45px] group"
                            onClick={() => {
                              setShowConnectForm({ isOpen: false });
                            }}
                          >
                            <Link href="/subscription">
                              <div className="flex items-center gap-4 font-bold text-black group-hover:text-black">
                                {t("Upgrade to Elite")}
                              </div>
                            </Link>
                          </AnimatedButton>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <div className="flex items-center justify-end gap-2">
                    {selectedBroker && (
                      <div className="flex items-center justify-end gap-2">
                        {selectedBroker.type === "live" ? (
                          <Badge className="bg-[--marketplace-green]">
                            {t("Live Trading")}
                          </Badge>
                        ) : (
                          <Badge className="bg-[--accent-primary]">
                            {t("Virtual Trading")}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  {/* For Alpaca (Virtual Trading) */}
                  <TabsContent value={"4"}>
                    <div className="flex flex-col gap-2  mt-0 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.ALPACA.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div className="border-b pb-2 flex flex-col gap-4">
                          <p className="text-sm">
                            {t(
                              "Start trading risk-free with a free API connection and a paper trading account. Explore the platform, test ideas, and build automated strategies with zero pressure.",
                            )}
                          </p>
                          <p className="text-sm">
                            {t(
                              "Open your free account, connect your API, and start experimenting in minutes. When you’re ready, switch to live trading with real capital.",
                            )}
                          </p>
                        </div>
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Open an Alpaca account")}
                          </CardTitle>
                          <CardDescription>
                            <a
                              href={"https://app.alpaca.markets/signup"}
                              className="underline"
                              target="_blank"
                            >
                              {t("Click here")}
                            </a>{" "}
                            {t(
                              "to open a free Virtual Trading account with Alpaca",
                            )}
                          </CardDescription>
                        </div>
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your API credentials below to enable fully automated trading",
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("API Key"),
                          t("API Key is Required"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "apiSecret",
                          t("Secret"),
                          t(" API secret is required"),
                          brokerConnected,
                          "********",
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Alpaca (Virtual Trading) */}
                  <TabsContent value={"26"}>
                    <div className="flex flex-col gap-2  mt-0 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.STERLING.DARK.textLogo}
                          alt={"sterling-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Open an Sterling account")}
                          </CardTitle>
                          <CardDescription>
                            <a
                              href={"https://sterlingtradingtech.com/"}
                              className="underline"
                              target="_blank"
                            >
                              {t("Click here")}
                            </a>{" "}
                          </CardDescription>
                        </div>
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your API credentials below to enable fully automated trading",
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("Station"),
                          t("Station is required"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "passphrase",
                          t("Password"),
                          t("Password is required"),
                          brokerConnected,
                          "********",
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Ctrader (Virtual Trading) */}
                  <TabsContent value={"2"}>
                    <div className="flex flex-col gap-2  mt-0 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.CTRADER.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1.  Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your API credentials below to enable fully automated trading",
                            )}
                          </CardDescription>
                        </div>
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Select the account you want to connect")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Choose the broker account you’d like to link to Level2",
                            )}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-end justify-between gap-2">
                          <div className="flex-1">
                            {renderInputField(
                              "apiKey",
                              t("Access Token"),
                              t("Acces Token is required"),
                              brokerConnected,
                              "*****",
                            )}
                          </div>
                          <Button
                            disabled={brokerConnected}
                            onClick={(e) => {
                              e.preventDefault();
                              handleCtraderConnect();
                            }}
                            loading={ctraderLoading}
                          >
                            {t("Connect")}
                          </Button>
                        </div>
                        <FormField
                          control={connectForm.control}
                          name="apiSecret"
                          rules={{
                            required:
                              !brokerConnected && t("Please select one option"),
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("Select Account")}</FormLabel>

                              <FormControl>
                                <Select
                                  disabled={!accounts?.length} // ✅ Disable Select if accounts is empty
                                  onValueChange={field.onChange}
                                  value={field.value}
                                >
                                  <SelectTrigger
                                    disabled={!accounts?.length} // ✅ Disable trigger as well
                                    className="w-full"
                                  >
                                    <SelectValue
                                      placeholder={t("Select Account")}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectGroup>
                                      {accounts?.map(
                                        (
                                          account: {
                                            account_id: string;
                                            balance: string;
                                          },
                                          index: number,
                                        ) => (
                                          <SelectItem
                                            value={account.account_id.toString()}
                                            key={`account-${index}`}
                                          >
                                            {account.account_id}{" "}
                                            {`($${account.balance})`}
                                          </SelectItem>
                                        ),
                                      )}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Lightspeed (Virtual Trading) */}
                  <TabsContent value={"13"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className="flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.LIGHTSPEED.DARK.icon}
                          alt={"lightspeed-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        {/* 4 */}
                        <div>
                          <CardDescription>
                            {t(
                              "Please note: This process uses single sign-on with your",
                            )}
                            <Link
                              className="text-[--accent-secondary] underline"
                              href={
                                "https://lightspeed.com/demo-request/lightspeed-web-mobile"
                              }
                              target="_blank"
                            >
                              {" "}
                              Lightspeed Web & Mobile
                            </Link>
                            {t(
                              " account credentials for convenience. A Lightspeed Connect API key is not required and the Lightspeed Trader Pro desktop is currently not supported.",
                            )}
                          </CardDescription>
                        </div>{" "}
                        {/* 1 */}
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Click Connect")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Click the Connect button below to begin linking your Lightspeed Web & Mobile account.",
                            )}
                          </CardDescription>
                        </div>
                        {/* 2 */}
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Sign in to Lightspeed")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "You’ll be redirected to the Lightspeed login page to securely sign in and authorize access.",
                            )}
                          </CardDescription>
                        </div>
                        {/* 3 */}
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Complete the connection")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Once you’ve logged in, you’ll be automatically returned to Level2, where your Lightspeed account will be securely connected.",
                            )}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Fennel (Virtual Trading) */}
                  <TabsContent value={"17"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.FENNEL.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />

                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Open a Fennel account")}
                          </CardTitle>
                          <CardDescription>
                            <Link
                              target="_blank"
                              href={"https://fennel.com/"}
                              className="underline"
                            >
                              {t("Click here")}
                            </Link>{" "}
                            {t("to open a free Trading account.")}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Subscribe for access")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Choose your subscription plan and subscribe for API access",
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your Fennel Personal Access Token and then select your preferred trading account to establish a connection between Level2 and Fennel",
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-end justify-between gap-2">
                          <div className="flex-1">
                            {renderInputField(
                              "apiSecret",
                              t("Fennel Token"),
                              t("Fennel Token is required"),
                              brokerConnected,
                              "********",
                            )}
                          </div>
                          <Button
                            disabled={brokerConnected}
                            onClick={(e) => {
                              e.preventDefault();
                              handleFennelConnect();
                            }}
                            loading={fennelLoading}
                          >
                            {t("Connect")}
                          </Button>
                        </div>
                        <FormField
                          control={connectForm.control}
                          name="apiKey"
                          rules={{
                            required:
                              !brokerConnected && t("Please select one option"),
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("Select Account")}</FormLabel>

                              <FormControl>
                                <Select
                                  disabled={!fennelAccounts?.length} // ✅ Disable Select if accounts is empty
                                  onValueChange={field.onChange}
                                  value={field.value}
                                >
                                  <SelectTrigger
                                    disabled={!fennelAccounts?.length} // ✅ Disable trigger as well
                                    className="w-full"
                                  >
                                    <SelectValue
                                      placeholder={t("Select Account")}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectGroup>
                                      {fennelAccounts?.map(
                                        (
                                          account: {
                                            id: string;
                                            name: string;
                                          },
                                          index: number,
                                        ) => (
                                          <SelectItem
                                            value={account.id.toString()}
                                            key={`account-${index}`}
                                          >
                                            {account.name}{" "}
                                          </SelectItem>
                                        ),
                                      )}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Match-trade (Virtual Trading) */}
                  <TabsContent value={"1"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.MATCHTRADE.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Open a MatchTrader account")}
                          </CardTitle>
                          <CardDescription>
                            <Link
                              target="_blank"
                              className="underline"
                              href={
                                "https://match-trader.com/download/#try-demo"
                              }
                            >
                              {t("Click here")}
                            </Link>{" "}
                            {t("to open a free Virtual Trading account")}
                          </CardDescription>
                        </div>
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your API credentials below to enable fully automated trading",
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("Match-Trader Registered Email"),
                          t("Match-Trader Registered Email"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "passphrase",
                          t("Match-Trader Password"),
                          t("Match-Trader Password"),
                          brokerConnected,
                          "********",
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Meta Trader (Live Trading) */}
                  <TabsContent value={"14"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.MT5.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Get your MT5 credentials")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Contact your broker or check your client dashboard.",
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Connect your account")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your MT5 login details below to enable automated trading. ",
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("Login"),
                          t("Login"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "passphrase",
                          t("Master Password"),
                          t("Master Password"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "apiSecret",
                          t("Server"),
                          t("Server"),
                          brokerConnected,
                          "********",
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Meta Trader (Live Trading) */}
                  <TabsContent value={"28"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.PEPPERSTONE.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Get your MT5 credentials")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Contact your broker or check your client dashboard.",
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Connect your account")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your MT5 login details below to enable automated trading. ",
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("Login"),
                          t("Login"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "passphrase",
                          t("Master Password"),
                          t("Master Password"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "apiSecret",
                          t("Server"),
                          t("Server"),
                          brokerConnected,
                          "********",
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Meta Trader (Live Trading) */}
                  <TabsContent value={"15"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.FTMO.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Get your FTMO credentials")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Contact your broker or check your client dashboard.",
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Connect your account")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your FTMO login details below to enable automated trading. ",
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("Login"),
                          t("Login"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "passphrase",
                          t("Master Password"),
                          t("Master Password"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "apiSecret",
                          t("Server"),
                          t("Server"),
                          brokerConnected,
                          "********",
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Tradezero (Virtual Trading) */}
                  <TabsContent value={"5"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.TRADEZERO.DARK.icon}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />

                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Click Connect to begin")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Click the Connect button below to begin linking your TradeZero account",
                            )}
                          </CardDescription>
                        </div>

                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Sign in to TradeZero")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "You’ll be redirected to the TradeZero login page to securely sign in and authorize access",
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Complete the connection")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Once you’ve logged in, you’ll automatically return to Level2, where your TradeZero account will be securely connected",
                            )}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Trade station  */}
                  <TabsContent value={"18"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.TRADESTATION.DARK.textLogo}
                          alt={"Trade Station"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />

                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Click Connect to begin")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Click the Connect button below to begin linking your Trade Station account",
                            )}
                          </CardDescription>
                        </div>

                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Sign in to Trade Station")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "You’ll be redirected to the Trade Station login page to securely sign in and authorize access",
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Complete the connection")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Once you’ve logged in, you’ll automatically return to Level2, where your Trade Station account will be securely connected",
                            )}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Trade station  */}
                  <TabsContent value={"19"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.PUBLIC.DARK.textLogo}
                          alt={"Public"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />

                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Click Connect to begin")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Click the Connect button below to begin linking your Public account",
                            )}
                          </CardDescription>
                        </div>

                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Sign in to Public")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "You’ll be redirected to the Public login page to securely sign in and authorize access",
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Complete the connection")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Once you’ve logged in, you’ll automatically return to Level2, where your Public account will be securely connected",
                            )}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Webull  */}
                  <TabsContent value={"20"}>
                    <div className="flex flex-col gap-6 pt-4 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.WEBBULLS.DARK.textLogo}
                          alt={"Webull"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />

                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Click Connect to begin")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Click the Connect button below to begin linking your Webull  account",
                            )}
                          </CardDescription>
                        </div>

                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Sign in to Webull")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "You’ll be redirected to the Webull login page to securely sign in and authorize access",
                            )}
                          </CardDescription>
                        </div>
                        <div className="">
                          <CardTitle className="text-base text-muted-foreground">
                            {t("3. Complete the connection")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Once you’ve logged in, you’ll automatically return to Level2, where your Webull account will be securely connected",
                            )}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* For Leverate  */}
                  <TabsContent value={"11"}>
                    <div className="flex flex-col gap-2  mt-0 px-4">
                      <div className=" flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.LEVERATE.DARK.textLogo}
                          alt={"alpaca-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                      </div>
                      <div>
                        <CardTitle className="text-base text-muted-foreground">
                          {t("1. Open a Leverate account ")}
                        </CardTitle>
                        <CardDescription>
                          {t(
                            "Contact the Leverate team directly to open a Trading account.",
                          )}
                        </CardDescription>
                      </div>

                      <div className="">
                        <CardTitle className="text-base text-muted-foreground">
                          {t("2. Connect to the API")}
                        </CardTitle>
                        <CardDescription>
                          {t(
                            "Enter your API credentials below to enable fully automated trading",
                          )}
                        </CardDescription>
                      </div>
                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("Server ID "),
                          t("Server ID "),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "apiSecret",
                          t("Account ID"),
                          t("Account ID "),
                          brokerConnected,
                          "********",
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* For IG */}
                  <TabsContent value={"30"}>
                    <div className="flex flex-col gap-2 mt-0 px-4">
                      <div className="flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.IG.DARK.textLogo}
                          alt={"ig-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Open an IG account")}
                          </CardTitle>
                          <CardDescription>
                            <a
                              href={"https://www.ig.com/"}
                              className="underline"
                              target="_blank"
                              rel="noreferrer"
                            >
                              {t("Click here")}
                            </a>{" "}
                            {t("to open an account with IG")}
                          </CardDescription>
                        </div>
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your API credentials below to enable fully automated trading",
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {renderInputField(
                          "apiKey",
                          t("Username"),
                          t("Username is required"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "passphrase",
                          t("Password / Passphrase"),
                          t("Password is required"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "apiSecret",
                          t("API Key"),
                          t("API Key is required"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "accountId",
                          t("Account ID"),
                          t("Account ID is required"),
                          brokerConnected,
                          "********",
                        )}
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
              {SCTab != "" && !["UPGRADE", "ELITE"].includes(SCTab) && (
                <CardFooter className="grow px-0 gap-4 flex-col mt-10 md:mt-auto min-h-[105px]">
                  <div className="flex w-full h-full">
                    <div className="flex flex-col w-full float-right h-full">
                      <div className="flex flex-col w-full lg:flex-row justify-end">
                        {false ? (
                          <div className="flex w-full justify-center">
                            <Loader />
                          </div>
                        ) : (
                          <div className="w-full flex items-center justify-end">
                            <Button
                              disabled={
                                isSaving || (!brokerConnected && !isFormValid)
                              }
                              loadingText={isSaving}
                              variant={
                                brokerConnected ? "destructive" : "default"
                              }
                              className={cn("text-sm gap-2 flex items-center")}
                            >
                              {isSaving ? (
                                <>
                                  {brokerConnected
                                    ? t("Revoking")
                                    : t("Connecting")}
                                </>
                              ) : (
                                <>
                                  {brokerConnected ? t("Revoke") : t("Connect")}
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>

                      {["4"].includes(SCTab) && (
                        <div className="text-xs text-muted-foreground flex gap-1 items-center">
                          {t("Not sure how to connect the API.")}{" "}
                          <Button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              if (SCTab == "1")
                                window.open(
                                  "https://learn.trylevel2.com/docs/technical/trading-with-match-trade",
                                  "_blank",
                                );
                              else if (SCTab == "2") {
                                window.open(
                                  "https://learn.trylevel2.com/docs/technical/getting-started-with-cTrader",
                                );
                              } else
                                setShowGuideModal({ isOpen: true, link: "" });
                            }}
                            variant="link"
                            className="p-0 text-xs text-[var(--accent-primary)]"
                          >
                            {t("Click here")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardFooter>
              )}
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <RevokeModal
        isOpen={revokeOpen}
        onClose={() => setRevokeOpen(false)}
        onConfirm={() => handleRevokeBrokerKeys()}
      ></RevokeModal>
    </>
  );
}

export default BrokerConnectModal;
